import { jsx, jsxs } from "react/jsx-runtime";
import { Box, Button, IconArrowRight } from "@zillow/constellation";
import { useYieldCallback } from "@zillow/yield-callback";
import PropTypes from "prop-types";
import React__default from "react";
import { isBrowser } from "../../utils/utilities.esm.js";
import OverlappingStack from "../PAFormStepper/OverlappingStack.esm.js";
import { ListItemWithProgress, ImageWithProgress } from "./ProgressTracker.esm.js";
const MediaStepper = ({
  id,
  animationTime,
  buttonText,
  children,
  collapseOthers,
  onButtonClick,
  reverse,
  onStepClick
}) => {
  const [skipAnimation, setSkipAnimation] = React__default.useState(false);
  const [activeNumber, setActiveNumber] = React__default.useState(0);
  const intervalId = React__default.useRef(void 0);
  const [hashNavigated, setHashNavigated] = React__default.useState(false);
  const scrollToIdRef = React__default.useRef(null);
  const clearCurrentInterval = React__default.useCallback(() => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
      intervalId.current = void 0;
    }
  }, []);
  const onPropClick = useYieldCallback((propNumber, div) => {
    setSkipAnimation(true);
    setActiveNumber(propNumber);
    clearCurrentInterval();
    onStepClick == null ? void 0 : onStepClick(div);
  }, [clearCurrentInterval, onStepClick]);
  const items = React__default.useMemo(() => React__default.Children.toArray(children).filter((child) => child.type === MediaStepper.Item), [children]);
  const handleHashNavigation = React__default.useCallback(() => {
    if (!isBrowser) {
      return;
    }
    const hash = window.location.hash;
    if (!hash) {
      return;
    }
    const targetId = hash.substring(1);
    const itemIndex = items.findIndex((item) => item.props.id && item.props.id === targetId);
    if (itemIndex === -1) {
      return;
    }
    setActiveNumber(itemIndex);
    setSkipAnimation(true);
    setHashNavigated(true);
    scrollToIdRef.current = targetId;
    clearCurrentInterval();
  }, [items, clearCurrentInterval]);
  React__default.useEffect(() => {
    handleHashNavigation();
  }, [handleHashNavigation]);
  React__default.useLayoutEffect(() => {
    if (!scrollToIdRef.current) {
      return;
    }
    const targetId = scrollToIdRef.current;
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
      const itemIndex = items.findIndex((item) => item.props.id === targetId);
      if (itemIndex === -1) {
        return;
      }
      setActiveNumber(itemIndex);
    }
    scrollToIdRef.current = null;
  }, [items]);
  React__default.useEffect(() => {
    if (!isBrowser) {
      return;
    }
    window.addEventListener("hashchange", handleHashNavigation);
    return () => {
      window.removeEventListener("hashchange", handleHashNavigation);
    };
  }, [handleHashNavigation]);
  React__default.useEffect(() => {
    if (!animationTime || animationTime < 0 || hashNavigated) {
      return;
    }
    clearCurrentInterval();
    intervalId.current = setInterval(() => {
      setActiveNumber((prevActiveNumber) => (prevActiveNumber + 1) % items.length);
    }, 1e3 * animationTime);
    return clearCurrentInterval;
  }, [animationTime, hashNavigated, items.length, clearCurrentInterval]);
  const onButtonClickYielded = useYieldCallback(() => {
    onButtonClick == null ? void 0 : onButtonClick();
  }, [onButtonClick]);
  const renderListItems = React__default.useCallback((item, index) => {
    const active = index === activeNumber;
    return /* @__PURE__ */ jsx(React__default.Fragment, { children: /* @__PURE__ */ jsx(ListItemWithProgress, { heading: item.props.heading, active, animationTime, skipAnimation, onClick: (div) => {
      onPropClick(index, div);
    }, collapseOthers, id: item.props.id, children: item.props.children }) }, index);
  }, [activeNumber, animationTime, skipAnimation, onPropClick, collapseOthers]);
  const renderImageItems = React__default.useCallback((item, index) => /* @__PURE__ */ jsx(ImageWithProgress, { active: activeNumber === index, media: item.props.image }, index), [activeNumber]);
  return /* @__PURE__ */ jsx("div", { id, children: /* @__PURE__ */ jsxs(Box, { sx: {
    gap: "$spacing.sm",
    display: "grid",
    gridTemplateColumns: "1fr",
    "@media $lg_gte": {
      gap: "$spacing.md",
      gridTemplateColumns: "1fr 1fr"
    }
  }, children: [
    /* @__PURE__ */ jsxs(Box, { sx: {
      ...reverse && {
        order: 1
      },
      display: "flex",
      gap: "$spacing.lg",
      "flex-direction": "column",
      "margin-right": "$spacing.none",
      "@media $xl_gte": {
        "margin-right": "15%"
      }
    }, children: [
      items.map(renderListItems),
      !!buttonText && /* @__PURE__ */ jsx(Box, { as: Button, sx: {
        marginTop: "$spacing.lg !important",
        alignSelf: "flex-start"
      }, buttonType: "primary", iconPosition: "end", icon: /* @__PURE__ */ jsx(IconArrowRight, {}), onClick: onButtonClickYielded, children: buttonText })
    ] }),
    /* @__PURE__ */ jsx(OverlappingStack, { sx: {
      ...!reverse && {
        justifyContent: "end"
      },
      "@media $md_lte": {
        display: "none"
      }
    }, children: items.map(renderImageItems) })
  ] }) });
};
MediaStepper.Item = () => null;
MediaStepper.Item.displayName = "MediaStepper.Item";
MediaStepper.Item.propTypes = {
  /** Child content to be displayed for this step */
  children: PropTypes.node,
  /** Heading text for this step */
  heading: PropTypes.string.isRequired,
  /** Image to be displayed for this step */
  image: PropTypes.shape({
    /** Alt text for the image */
    alt: PropTypes.string.isRequired,
    /** Image source URL */
    src: PropTypes.string.isRequired,
    /** Image width in pixels */
    width: PropTypes.number.isRequired,
    /** Image height in pixels */
    height: PropTypes.number.isRequired
  }).isRequired,
  /** Optional ID for hash navigation and accessibility */
  id: PropTypes.string
};
MediaStepper.defaultProps = {
  animationTime: 6,
  collapseOthers: false
};
MediaStepper.propTypes = {
  /** Optional ID for hash navigation and accessibility */
  id: PropTypes.string,
  /** Time in seconds between auto-advancing steps (set to 0 or negative to disable) */
  animationTime: PropTypes.number,
  /** Text for the call to action button (if not provided, no button will be shown) */
  buttonText: PropTypes.string,
  /** MediaStepper.Item components representing each step */
  children: PropTypes.node.isRequired,
  /** Whether to collapse other items when one is expanded */
  collapseOthers: PropTypes.bool.isRequired,
  /** Required heading for the component */
  heading: PropTypes.string.isRequired,
  /** Handler called when the button is clicked */
  onButtonClick: PropTypes.func,
  /** Whether to reverse the layout (content on right, images on left) */
  reverse: PropTypes.bool,
  /** Handler called when a step is clicked */
  onStepClick: PropTypes.func
};
export {
  MediaStepper as default
};
